import React, { useState, useEffect } from 'react';
import type { Field } from './CustomTableConditionWithMultipleFields';
import Icons from '../../../../assets/index';
import Dropdown from '@/components/Dropdown/Dropdown';

import Button from '@/components/Button/Button';

interface Option {
  id: string;
  label: string;
  value: string;
  fields: Field[];
}

interface EditOptionPanelProps {
  mode: 'add' | 'edit';
  option?: Option;
  onSave: (option: Option) => void;
  onCancel: () => void;
}

const actionOptions = [
  { label: 'Text Input', value: 'text_input' },
  { label: 'Text Area', value: 'text_area' },
  { label: 'File upload', value: 'file' },
  { label: 'Information', value: 'information' },
  { label: 'No Action', value: 'no_action' },
];

const getDefaultField = (id: string) => ({
  id,
  label: '',
  fieldType: '',
  required: true,
  additional_config: {
    editable: true,
    placeholder: '',
    defaultValue: '',
  },
});

const AddEditOptionPanel: React.FC<EditOptionPanelProps> = ({ mode, option, onSave, onCancel }) => {
  const [optionName, setOptionName] = useState('');
  const [fields, setFields] = useState<Field[]>([]);
  const [errors, setErrors] = useState<{ name?: string; fields: { [id: string]: string } }>({
    fields: {},
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && option) {
      setOptionName(option.label);
      setFields(option.fields && option.fields.length > 0 ? option.fields : [getDefaultField('1')]);
    } else {
      setOptionName('');
      setFields([getDefaultField('1')]);
    }
    setErrors({ fields: {} });
    setHasSubmitted(false);
  }, [mode, option]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setOptionName(e.target.value);
  const handleFieldTypeChange = (id: string, value: string) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, fieldType: value } : f)));
  };
  const handleFieldLabelChange = (id: string, value: string) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, label: value } : f)));
  };
  const handleAddAction = () => {
    setFields((prev) => [...prev, getDefaultField(Date.now().toString())]);
  };
  const handleDeleteAction = (id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  };
  const handleClearAll = () => {
    setOptionName('');
    setFields([getDefaultField('1')]);
    setErrors({ fields: {} });
  };
  const handleUpdate = () => {
    setHasSubmitted(true);
    const newErrors: { name?: string; fields: { [id: string]: string } } = { fields: {} };
    if (!optionName.trim()) newErrors.name = 'Option name is required.';
    fields.forEach((f) => {
      if (!f.label.trim()) newErrors.fields[f.id] = 'Action field name is required.';
      else if (!f.fieldType) newErrors.fields[f.id] = 'Action type is required.';
    });
    setErrors(newErrors);
    if (newErrors.name || Object.keys(newErrors.fields).length > 0) return;
    const optionData: any = {
      label: optionName,
      value: option?.value || optionName.toLowerCase().replace(/\s+/g, '_'),
      fields,
    };

    // Only include id for existing options (edit mode)
    if (option?.id) {
      optionData.id = option.id;
    }

    onSave(optionData);
  };

  return (
    <div className="flex flex-col gap-6 min-w-[400px] overflow-y-auto">
      {/* Option Name */}
      <div>
        <label className="text-xs font-semibold text-ds-gray-500">
          Option Name <span className="text-ds-red-400">*</span>
        </label>
        <input
          type="text"
          value={optionName}
          onChange={handleNameChange}
          className="w-full border rounded px-3 py-2 mt-1 text-xs"
          placeholder="This will show up as the label of the option."
        />
        {hasSubmitted && errors.name && (
          <div className="text-xs text-red-500 mt-1">{errors.name}</div>
        )}
      </div>
      {/* Action Fields */}
      <div>
        {fields.map((field, idx) => (
          <div key={field.id} className="mb-4 border-b pb-4 last:border-b-0 last:pb-0">
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-ds-gray-500">
                Action Field {idx + 1} <span className="text-ds-red-400">*</span>
              </label>
              {fields.length > 1 && (
                <button
                  className="text-red-600"
                  onClick={() => handleDeleteAction(field.id)}
                  title="Delete Action"
                >
                  <Icons.trashIcon />
                </button>
              )}
            </div>
            <div>
              <input
                type="text"
                value={field.label}
                onChange={(e) => handleFieldLabelChange(field.id, e.target.value)}
                placeholder="Action Field Name"
                className="w-full border rounded px-3 py-2 mt-1 text-xs mb-1"
              />
              <Dropdown
                options={actionOptions}
                selectedValue={field.fieldType}
                onChange={(val) => handleFieldTypeChange(field.id, val)}
                error={hasSubmitted && !!errors.fields[field.id]}
                errorMessage={hasSubmitted ? errors.fields[field.id] : undefined}
                isFullWidth
              />
            </div>
          </div>
        ))}
        <button
          type="button"
          className="text-blue-600 text-xs hover:text-blue-500 flex items-center gap-1 mt-2"
          onClick={handleAddAction}
        >
          <Icons.plusIcon className="w-3 h-3" /> Add Action
        </button>
      </div>
      {/* Bottom Buttons */}
      <div className="flex justify-between mt-8 gap-2">
        <button className="border px-4 py-2 rounded text-xs" onClick={handleClearAll}>
          Clear All
        </button>
        <div className="flex gap-2">
          <button className="border px-4 py-2 rounded text-xs" onClick={onCancel}>
            Cancel
          </button>
          <Button onClick={handleUpdate} color="primary">
            {mode === 'edit' ? 'Update' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddEditOptionPanel;
